import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Collapse, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';

import {
  DEFECT_SEVERITY_COLORS,
  DEFECT_SEVERITY_MEANINGS,
  DEFECT_SEVERITY_TEXT_COLORS,
  type DefectSeverity,
  defectSeverityLabel,
} from '~/components/compliance/defectSeverity';
import { ElevatorViolationNotes } from '~/components/compliance/detailPage/ElevatorViolationNotes';
import { toStoredCalendarDate } from '~/components/compliance/formatters';
import ResolveViolationDialog from '~/components/compliance/ResolveViolationDialog';
import type { Violation } from '~/data/schemas/car';
import { BUTTON_SX } from '~/theme';

const displayDate = (value: string | null | undefined) =>
  value ? format(parseISO(value), 'MMM d, yyyy') : '—';

const titleCase = (value: string | null) =>
  value
    ? value
        .replace(/_/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : null;

const statusColor = (status: string): 'error' | 'warning' | 'success' | 'default' => {
  switch (status.toLowerCase()) {
    case 'overdue':
      return 'error';
    case 'open':
      return 'warning';
    case 'resolved':
      return 'success';
    default:
      return 'default';
  }
};

/**
 * One violation: what it is, when it is due, what has been said about it, and the two
 * things you can do to it.
 *
 * The drawer's row put Notes, Edit and Resolve in bare `<Box onClick>`s no keyboard
 * could reach, and hid every note behind a modal. Notes start collapsed and the count
 * comes from the violation itself, so a list of them costs nothing to draw.
 */
export function ElevatorViolationItem({
  violation,
  certificateCarId,
  certificateCarName,
  onEdit,
  onNoteAdded,
}: {
  violation: Violation;
  certificateCarId: string;
  certificateCarName: string;
  /** `null` for a reader who can't edit violations, which removes the button. */
  onEdit: (() => void) | null;
  /** Refreshes the violation list so note counts follow what was written. */
  onNoteAdded: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const resolved = violation.status.toLowerCase() === 'resolved';

  // Segments are joined rather than templated so an absent one leaves no dangling
  // separator: LOLER (UK) defects carry no `code` at all.
  const heading = [titleCase(violation.responsibility), violation.location, violation.code]
    .filter(Boolean)
    .join(' · ');

  // `severity` is our own generic category (A/B/C) and is non-null exactly for LOLER
  // defects, so it doubles as the "is this a LOLER defect" test without plumbing the
  // locale down here. This is what a customer sees per entry — deliberately NOT the
  // inspection company's own code, which is one vendor's private vocabulary. (CX-416)
  //
  // Rendered as its own chip rather than as another " · " segment so it carries the
  // same category colour the analytics charts use, and so the letter reads as a
  // classification instead of as more free text. The colour never stands alone: the
  // chip is labelled, and its meaning is in the tooltip.
  //
  // The wire's value space is `'A' | 'B' | 'C' | '' | null | undefined`, so a truthy
  // check narrows it without a cast — an empty string is "no category", same as null.
  const severity: DefectSeverity | null = violation.severity ? violation.severity : null;

  // A violation nobody has written on has no count worth stating — the control is what
  // you use to say the first thing, so it says that instead of "0".
  const noteLabel =
    violation.noteCount === 0
      ? 'Add note'
      : violation.noteCount === 1
        ? '1 note'
        : `${violation.noteCount} notes`;

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
        <Box flex={1} minWidth={240}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mb={0.5}>
            <Chip
              size="small"
              label={violation.status}
              color={statusColor(violation.status)}
              variant={resolved ? 'outlined' : 'filled'}
            />
            <Typography variant="body2" fontWeight={500}>
              {heading}
            </Typography>
            {severity ? (
              // MUI Tooltip rather than the native `title`: it works on touch and can be
              // reached from the keyboard, which `title` can't. `describeChild` exposes
              // the meaning as the chip's description rather than replacing its name,
              // and the chip carries its own tab stop — without one a keyboard user
              // could never open the tooltip, which is the only place the category is
              // spelled out.
              <Tooltip title={DEFECT_SEVERITY_MEANINGS[severity]} describeChild>
                <Chip
                  size="small"
                  tabIndex={0}
                  label={defectSeverityLabel(severity)}
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    // Text takes the text-safe step (WCAG 4.5:1 for small type); the
                    // border keeps the mark colour, which only has to clear 3:1 as a
                    // graphical object.
                    color: DEFECT_SEVERITY_TEXT_COLORS[severity],
                    borderColor: DEFECT_SEVERITY_COLORS[severity],
                    bgcolor: 'transparent',
                  }}
                />
              </Tooltip>
            ) : null}
          </Stack>

          {violation.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {violation.description}
            </Typography>
          ) : null}

          <Stack direction="row" gap={3} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              {/* date_open's time is a parsing artifact, not a real instant like
                  resolvedDt — reduce it to a calendar date or a viewer west of
                  UTC sees the previous day (AMP-699). */}
              Opened {displayDate(toStoredCalendarDate(violation.dateOpen))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Correction due {displayDate(violation.correctionDue)}
            </Typography>
            {resolved ? (
              <Typography variant="caption" color="success.main">
                Resolved {displayDate(violation.resolvedDt)}
                {violation.resolutionType ? ` by ${titleCase(violation.resolutionType)}` : ''}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Stack direction="row" gap={1} flexShrink={0}>
          {onEdit ? (
            <Button size="small" startIcon={<EditOutlinedIcon />} onClick={onEdit} sx={BUTTON_SX}>
              Edit
            </Button>
          ) : null}
          <Button
            size="small"
            variant={resolved ? 'text' : 'outlined'}
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => setResolveOpen(true)}
            sx={BUTTON_SX}
          >
            {resolved ? 'Resolution' : 'Resolve'}
          </Button>
        </Stack>
      </Stack>

      <Button
        size="small"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        sx={{ ...BUTTON_SX, mt: 1, ml: -1 }}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          />
        }
      >
        {noteLabel}
      </Button>

      {/* Kept out of the tree until first opened, so a collapsed section costs nothing;
          `enabled` then holds the read for one that is closed again. */}
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
          <ElevatorViolationNotes
            violationId={violation.id}
            enabled={expanded}
            onNoteAdded={onNoteAdded}
          />
        </Box>
      </Collapse>

      <Divider sx={{ mt: 2 }} />

      {/* Mounted only while open: the dialog carries a form and an uploader. */}
      {resolveOpen ? (
        <ResolveViolationDialog
          open
          onClose={() => setResolveOpen(false)}
          violation={violation}
          certificateCarName={certificateCarName}
          certificateCarId={certificateCarId}
        />
      ) : null}
    </Box>
  );
}
